import { useState } from 'react';
import type { IsoDate, Tag, TaskInstance, User } from '@hestia/core';
import type { TaskList } from '../api.js';
import { TaskRow } from '../components/TaskRow.js';
import { Icon, icons, hueVar } from '../ui.js';
import { whenLabel } from '../lang.js';

interface Props {
  list: TaskList | null;
  date: IsoDate;
  me: User;
  viewing: User | null;
  /** The tag the whole app is filtered to, if any. */
  tag: Tag | null;
  /** Tags something uses — offered here as chips, because on a phone there is
   *  no sidebar to filter from. */
  usedTags: Tag[];
  users: Map<string, User>;
  tags: Map<string, Tag>;
  onTag: (id: string | null) => void;
  onToggle: (i: TaskInstance) => void;
  onOpen: (i: TaskInstance) => void;
  onMoveToToday: (i: TaskInstance) => void;
  onAdd: () => void;
}

/** How many finished tasks show before the rest fold away. The list is for what
 *  is still to do; what is done is there to be checked, not scanned past. */
const DONE_SHOWN = 5;

/** Every task, once each.
 *
 *  The day views answer "what is on today" and "what is on this day"; this
 *  answers "what is there". A task that repeats every day is one row here, with
 *  its rule; a one-off next March is as visible as one tomorrow. Sections run in
 *  order of what wants attention: what slipped, what is today, what is coming,
 *  the standing chores, and what is finished. */
export function Tasks({
  list, date, me, viewing, tag, usedTags, users, tags, onTag, onToggle, onOpen, onMoveToToday, onAdd,
}: Props) {
  const [allDone, setAllDone] = useState(false);
  const rowProps = { users, tags, today: date, onToggle, onOpen };

  const who = viewing ? (viewing.id === me.id ? 'Your tasks' : `${viewing.displayName}'s tasks`) : 'All tasks';
  const empty = !!list && !list.stillOpen.length && !list.today.length && !list.upcoming.length
    && !list.repeating.length && !list.done.length;
  const done = list ? (allDone ? list.done : list.done.slice(0, DONE_SHOWN)) : [];

  return (
    <div className="page">
      <header style={{ paddingTop: 6 }}>
        <div className="sec">Everything on the list</div>
        <h1 className="dsp" style={{ fontSize: 34, lineHeight: 1.08, margin: '9px 0 0' }}>
          {tag ? `${who} · ${tag.name}` : who}
        </h1>
      </header>

      {usedTags.length > 0 && (
        <div className="tag-filter" role="group" aria-label="Filter by tag">
          <button className={`tag-choice${tag ? '' : ' on'}`} aria-pressed={!tag} onClick={() => onTag(null)}>
            Any tag
          </button>
          {usedTags.map((t) => (
            <button key={t.id} className={`tag-choice${tag?.id === t.id ? ' on' : ''}`}
                    aria-pressed={tag?.id === t.id}
                    onClick={() => onTag(tag?.id === t.id ? null : t.id)}>
              <span className="tag-dot" style={{ background: hueVar(t.hue) }} />{t.name}
            </button>
          ))}
        </div>
      )}

      {!list && <div className="empty" style={{ justifyContent: 'center' }}>Loading…</div>}

      {list && list.stillOpen.length > 0 && (
        <section className="section">
          <div className="sec">Still open</div>
          {list.stillOpen.map((i) => (
            <TaskRow key={i.task.id} instance={i} {...rowProps} onMoveToToday={onMoveToToday} />
          ))}
        </section>
      )}

      {list && list.today.length > 0 && (
        <section className="section">
          <div className="sec">Today</div>
          {list.today.map((i) => <TaskRow key={i.task.id} instance={i} {...rowProps} />)}
        </section>
      )}

      {list && list.upcoming.length > 0 && (
        <section className="section">
          <div className="sec">Upcoming</div>
          {list.upcoming.map((i) => (
            <TaskRow key={i.task.id} instance={i} {...rowProps} when={whenLabel(i.date, date)} />
          ))}
        </section>
      )}

      {list && list.repeating.length > 0 && (
        <section className="section">
          {/* The standing chores, each once, at the next day it comes round. */}
          <div className="sec">Repeating</div>
          {list.repeating.map((i) => (
            <TaskRow key={i.task.id} instance={i} {...rowProps} when={`Next ${whenLabel(i.date, date)}`} />
          ))}
        </section>
      )}

      {list && list.done.length > 0 && (
        <section className="section">
          <div className="sec">Done</div>
          {done.map((i) => <TaskRow key={i.task.id} instance={i} {...rowProps} />)}
          {!allDone && list.done.length > DONE_SHOWN && (
            <button className="empty" onClick={() => setAllDone(true)}>Show more finished tasks</button>
          )}
        </section>
      )}

      {empty ? (
        <div className="empty" style={{ justifyContent: 'center', padding: '30px 19px' }}>
          {tag ? `Nothing tagged ${tag.name}.` : 'Nothing on the list.'}
        </div>
      ) : list && (
        <button className="empty" onClick={onAdd}>
          <Icon path={icons.plus} size={17} w={2.2} />Add a task
        </button>
      )}
    </div>
  );
}
